import { Doc } from "../../_generated/dataModel";
import { FilterRule, FilterRuleCtx } from "../../lib/types/filters_types";

const VALID_FORMATS = ["csv", "json", "pdf", "excel"] as const;

const VALID_DATA_SOURCES = [
  "sales",
  "clients",
  "products",
  "quotations",
  "invoices",
  "invoices_payments",
  "payments",
  "suppliers",
  "sellers",
  "work_orders",
  "work_order_equipment",
  "work_order_personnel",
  "work_order_products",
  "work_order_services",
  "work_order_expenses",
  "work_order_logs",
  "equipment",
  "personnel",
  "personnel_roles",
] as const;

/**
 * Filtra reportes por formato de salida (csv, json, pdf, excel).
 * Si el valor es "all", no se aplica filtro.
 */
export const formatRule: FilterRule<Doc<"reports">> = {
  priority: 3,
  build: async (
    ctx: FilterRuleCtx,
    args: Partial<Omit<Doc<"reports">, "_id" | "_creationTime">>,
  ) => {
    const { userContext } = ctx;
    const format = args.format as string | undefined;

    if (!format || format === "all") return undefined;
    if (!VALID_FORMATS.includes(format as any)) return undefined;
    if (!userContext?.companyId) {
      throw new Error("Usuario no tiene empresa asignada");
    }

    const reports = await ctx.db
      .query("reports")
      .withIndex("by_company_id", (q) =>
        q.eq("internalControl.companyId", userContext.companyId!),
      )
      .collect();

    return reports.filter((r) => r.format === format) as Doc<"reports">[];
  },
};

/**
 * Filtra reportes por fuente de datos (sales, clients, products, etc.).
 * Si el valor es "all", no se aplica filtro.
 */
export const dataSourceRule: FilterRule<Doc<"reports">> = {
  priority: 4,
  build: async (
    ctx: FilterRuleCtx,
    args: Partial<Omit<Doc<"reports">, "_id" | "_creationTime">>,
  ) => {
    const { userContext } = ctx;
    const dataSource = args.dataSource as string | undefined;

    if (!dataSource || dataSource === "all") return undefined;
    if (!VALID_DATA_SOURCES.includes(dataSource as any)) return undefined;
    if (!userContext?.companyId) {
      throw new Error("Usuario no tiene empresa asignada");
    }

    const reports = await ctx.db
      .query("reports")
      .withIndex("by_company_id", (q) =>
        q.eq("internalControl.companyId", userContext.companyId!),
      )
      .collect();

    return reports.filter((r) => {
      if (r.dataSource === dataSource) return true;
      // Backward compat: "invoices_payments" y "payments" son equivalentes
      if (dataSource === "invoices_payments" && r.dataSource === "payments")
        return true;
      if (dataSource === "payments" && r.dataSource === "invoices_payments")
        return true;
      return false;
    }) as Doc<"reports">[];
  },
};
